// React Query hooks for fetching email signature data

import { useApiClient } from '@commander/api-client';
import { useQuery } from '@tanstack/react-query';
import type { EmailSignatureListParams } from '../../api/emailSignatureApi';
import { createEmailSignatureApi } from '../../api/emailSignatureApi';
import { communicationKeys } from '../communicationKeys';

/**
 * Hook to fetch paginated list of email signatures
 * Uses React Query for automatic caching, refetching, and loading states
 */
export function useEmailSignatures(
  params?: EmailSignatureListParams,
  options?: { enabled?: boolean; refetchInterval?: number | false }
) {
  const apiClient = useApiClient();
  const signatureApi = createEmailSignatureApi(apiClient);

  return useQuery({
    queryKey: communicationKeys.signatures.list(params),
    queryFn: () => signatureApi.getEmailSignatures(params),
    enabled: options?.enabled !== false,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Hook to fetch a single email signature by ID
 */
export function useEmailSignature(signatureId: string, options?: { enabled?: boolean }) {
  const apiClient = useApiClient();
  const signatureApi = createEmailSignatureApi(apiClient);

  return useQuery({
    queryKey: communicationKeys.signatures.detail(signatureId),
    queryFn: () => signatureApi.getEmailSignature(signatureId),
    enabled: options?.enabled !== false && !!signatureId,
  });
}
